import Layout from '@/components/feature/Layout';
import { companyInfo } from '@/mocks/company';
import { products } from '@/mocks/products';
import { Link } from 'react-router-dom';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Modelos de Máquinas de Solda de Geomembrana',
  description: 'Compare todos os modelos de máquinas de solda geomembrana disponíveis: cunha quente e extrusoras.',
  url: 'https://maquinadesoldageomembrana.com.br/modelos-de-maquinas',
  inLanguage: 'pt-BR',
};

const whatsappMessage = encodeURIComponent('Olá! Estou no site da Maquinasdesoldageomembrana.com.br e gostaria de informações sobre máquinas de solda de geomembrana.');
const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${whatsappMessage}`;

export default function ModelosPage() {
  const all = products;
  const cunhaQuente = products.filter((p) => p.category === 'Cunha Quente');
  const extrusoras = products.filter((p) => p.category === 'Extrusora');
  const acessorios = products.filter((p) => p.category === 'Acessório');

  const ComparisonTable = ({ items }: { items: typeof products }) => (
    <div className="overflow-x-auto rounded-xl border border-background-200/70 bg-white mb-8">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-foreground-900 text-white">
            <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wide whitespace-nowrap">Modelo</th>
            <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wide whitespace-nowrap">Potência</th>
            <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wide whitespace-nowrap">Espessura</th>
            <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wide whitespace-nowrap">Velocidade</th>
            <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wide whitespace-nowrap">Temperatura</th>
            <th className="text-center px-4 py-3 font-bold text-xs uppercase tracking-wide whitespace-nowrap">PEAD</th>
            <th className="text-center px-4 py-3 font-bold text-xs uppercase tracking-wide whitespace-nowrap">PVC</th>
            <th className="text-center px-4 py-3 font-bold text-xs uppercase tracking-wide whitespace-nowrap">LLDPE</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p, i) => (
            <tr key={p.id} className={`border-t border-background-200/50 ${i % 2 === 0 ? 'bg-white' : 'bg-background-50/60'}`}>
              <td className="px-4 py-3 font-bold text-foreground-950 whitespace-nowrap">
                <Link to={`/modelos-de-maquinas/${p.slug}`} className="hover:text-primary-600 transition-colors cursor-pointer inline-flex items-center gap-1">
                  {p.name}
                </Link>
                {p.badge && <span className="ml-2 text-[10px] bg-primary-500 text-white px-1.5 py-0.5 rounded font-bold uppercase">{p.badge}</span>}
              </td>
              <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{p.specs.potencia}</td>
              <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{p.specs.material}</td>
              <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{p.specs.velocidade}</td>
              <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{p.specs.temperatura}</td>
              <td className="px-4 py-3 text-center">{p.compatibilidade.pead ? <i className="ri-check-line text-primary-600" /> : <i className="ri-close-line text-foreground-300" />}</td>
              <td className="px-4 py-3 text-center">{p.compatibilidade.pvc ? <i className="ri-check-line text-primary-600" /> : <i className="ri-close-line text-foreground-300" />}</td>
              <td className="px-4 py-3 text-center">{p.compatibilidade.lldpe ? <i className="ri-check-line text-primary-600" /> : <i className="ri-close-line text-foreground-300" />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <nav className="text-xs text-foreground-400 mb-6">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line mx-1" />
          <span className="text-foreground-600">Modelos de Máquinas</span>
        </nav>

        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-4">
          Modelos de Máquinas de Solda de Geomembrana
        </h1>
        <p className="text-foreground-500 text-sm md:text-base max-w-2xl mb-10">
          Compare todos os modelos disponíveis em estoque. Especificações técnicas reais, compatibilidade de materiais e potência para cada aplicação.
        </p>

        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 uppercase mb-6">Catálogo em Fotos</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6 mb-12" data-product-shop>
          {products.map((product) => (
            <div key={product.id} className="group bg-white rounded-2xl overflow-hidden border border-background-200/70 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-primary-300">
              <div className="relative w-full h-48 md:h-52 bg-gradient-to-br from-background-100 to-background-200 flex items-center justify-center p-5">
                {product.badge && (
                  <span className="absolute top-3 left-3 bg-primary-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">{product.badge}</span>
                )}
                <img
                  alt={`${product.name} — máquina de solda geomembrana`}
                  className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-500"
                  src={product.image}
                />
              </div>
              <div className="p-4 md:p-5 flex flex-col flex-1">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-1 uppercase">{product.name}</h3>
                <p className="text-foreground-500 text-xs mb-3">{product.category}</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="flex items-center gap-2 bg-background-100 rounded-lg px-3 py-2">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-flashlight-line text-primary-500 text-sm" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-foreground-900 text-[10px] uppercase tracking-wide leading-none mb-0.5">Potência</p>
                      <p className="text-foreground-950 text-xs truncate">{product.specs.potencia}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-background-100 rounded-lg px-3 py-2">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-speed-line text-primary-500 text-sm" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-foreground-900 text-[10px] uppercase tracking-wide leading-none mb-0.5">Velocidade</p>
                      <p className="text-foreground-950 text-xs truncate">{product.specs.velocidade}</p>
                    </div>
                  </div>
                </div>
                <Link
                  to={`/modelos-de-maquinas/${product.slug}`}
                  className="mt-auto flex items-center justify-center gap-2 border-2 border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-white text-xs md:text-sm px-4 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap uppercase tracking-wide font-bold"
                >
                  Ver Informações
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-8">
          <h2 className="text-sm font-bold text-foreground-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-primary-500 inline-block" />
            Todos os Modelos ({all.length})
          </h2>
          <ComparisonTable items={all} />
        </div>

        <div className="mb-8">
          <h2 className="text-sm font-bold text-foreground-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-primary-500 inline-block" />
            Cunha Quente ({cunhaQuente.length} modelos)
          </h2>
          <ComparisonTable items={cunhaQuente} />
        </div>

        <div className="mb-8">
          <h2 className="text-sm font-bold text-foreground-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-accent-500 inline-block" />
            Extrusoras ({extrusoras.length} modelos)
          </h2>
          <ComparisonTable items={extrusoras} />
        </div>

        {acessorios.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-bold text-foreground-900 uppercase tracking-wide mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-secondary-500 inline-block" />
              Acessórios ({acessorios.length})
            </h2>
            <ComparisonTable items={acessorios} />
          </div>
        )}

        <div className="bg-primary-50 rounded-2xl border border-primary-200/70 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950 uppercase mb-1">Não sabe qual modelo escolher?</h3>
            <p className="text-foreground-500 text-sm">Nossa equipe técnica analisa seu projeto e indica o equipamento ideal.</p>
          </div>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm px-8 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide flex-shrink-0"
          >
            <i className="ri-whatsapp-line" /> Consultar Especialista
          </a>
        </div>
      </div>
    </Layout>
  );
}